/**
 * LIST SOME OF A HOLDING ON THE SECONDARY MARKET.
 *
 * ## The first question is "how many", and that is the whole design
 *
 * "Do not automatically list the entire token balance." The field starts EMPTY, not at the full
 * holding, and there is no "list everything" default anywhere on this screen. Defaulting to the whole
 * balance is how somebody sells their entire interest in their own building in two taps.
 *
 * ## What this screen is not allowed to be
 *
 * It is not the control on over-listing. `tokenization.enforce_listing_capacity` (race-critical guard
 * 8, migration 0055) takes a `FOR UPDATE` lock on the seller's register rows before it reads the
 * totals, so two concurrent listings of the last of a holding cannot both succeed - and that holds
 * whether or not this form ever checked anything.
 *
 * What the arithmetic below buys is that the seller is told the number BEFORE they submit, in tokens
 * rather than in a constraint name. The comparison runs through BigInt, never a float: a holding is
 * up to eighteen digits of minor units and `Number.MAX_SAFE_INTEGER` is sixteen.
 *
 * ## The marketplace it lists on
 *
 * `txn.p2p_offer`, the order book that already exists, with the asset code `TOK:<token_id>` that the
 * schema has permitted since migration 0001. There is no second marketplace for tokenized assets, and
 * building one would have meant a second escrow path and a second set of race conditions.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { TierGate } from '@/components/gate';
import { ErrorState, LoadingCard } from '@/components/states';
import { api, LipaApiError, type TokenHolding } from '@/lib/api';
import { formatMinor, formatUnits, toMinorUnits, unitsFromMinor } from '@/lib/tokenization';

export default function ListTokens() {
  return (
    <TierGate requires="T2" what="Listing tokens on the marketplace">
      <Form />
    </TierGate>
  );
}

function Form() {
  const { tokenId = '' } = useParams();
  const navigate = useNavigate();

  const [holding, setHolding] = useState<TokenHolding | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // EMPTY. Never the full balance. See the note at the top of this file.
  const [tokens, setTokens] = useState('');
  const [price, setPrice] = useState('');
  const [minimum, setMinimum] = useState('');
  const [note, setNote] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ units: string; remaining: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const all = await api.tokenization.portfolio();
      const mine = all.find((h) => h.token_id === tokenId);
      if (!mine) {
        setLoadError('We have no record of that token on your account.');
        return;
      }
      setHolding(mine);
      setPrice((prev) => prev || (mine.unit_value ?? ''));
    } catch (e) {
      setLoadError(
        e instanceof LipaApiError ? e.detail.message : 'We could not load your holding.',
      );
    }
  }, [tokenId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The arithmetic, through BigInt. `available` is what is left after everything already listed, and
   * the total is `tokens * price` in minor units - a multiplication that would lose shillings on a
   * large property if it went through a double.
   */
  const sums = useMemo(() => {
    if (!holding) return null;
    const wanted = toMinorUnits(tokens || '0');
    const available = toMinorUnits(holding.units_available);
    const min = toMinorUnits(minimum || tokens || '0');

    const unitPrice = /^\d+$/.test(price) ? BigInt(price) : 0n;
    const total = (wanted * unitPrice) / 100_000_000n;

    return {
      wanted,
      available,
      remaining: available > wanted ? available - wanted : 0n,
      total: total.toString(),
      tooMuch: wanted > available,
      minTooBig: min > wanted,
    };
  }, [holding, tokens, price, minimum]);

  async function submit() {
    if (!holding) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.tokenization.list(holding.token_id, {
        tokens,
        unit_price: price,
        quote_asset: holding.quote_asset ?? 'KES',
        minimum: minimum || null,
        note: note || null,
      });
      setDone({ units: result.units, remaining: result.remaining_available });
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not create that listing.');
    } finally {
      setBusy(false);
    }
  }

  if (loadError) return <ErrorState body={loadError} />;
  if (!holding) return <LoadingCard lines={4} />;

  if (done) {
    return (
      <div className="mx-auto max-w-md space-y-6 py-6 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent/15 text-accent">
          <svg
            className="h-7 w-7"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.8}
              d="m5 13 4 4L19 7"
            />
          </svg>
        </div>
        <div>
          <h1 className="text-page-title text-ink">Listed</h1>
          <p className="mt-1 text-sm text-ink-muted">
            {formatUnits(done.units)} tokens are on the marketplace. You still hold{' '}
            {formatUnits(done.remaining)} that are not listed, and you can withdraw the listing at
            any time before it is taken.
          </p>
        </div>
        <div className="flex gap-2.5">
          <button
            onClick={() => navigate('/tokenize/portfolio')}
            className="flex-1 rounded-field border border-border py-3 text-sm font-medium text-ink transition hover:bg-surface-sunken"
          >
            Portfolio
          </button>
          <button
            onClick={() => navigate('/marketplace')}
            className="flex-1 rounded-field bg-primary py-3 text-sm font-medium text-white transition hover:bg-primary-light"
          >
            See it on the marketplace
          </button>
        </div>
      </div>
    );
  }

  const quote = holding.quote_asset ?? 'KES';
  const blocked = !tokens || !price || !!sums?.tooMuch || !!sums?.minTooBig;

  return (
    <div className="mx-auto max-w-lg space-y-5">
      <Link
        to="/tokenize/portfolio"
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <BackIcon /> Portfolio
      </Link>

      <header>
        <h1 className="text-page-title text-ink">List tokens for sale</h1>
        <p className="mt-0.5 text-sm text-ink-muted">
          {holding.underlying.description ?? holding.name} &middot; {holding.symbol}
        </p>
      </header>

      <section className="rounded-card border border-border bg-surface p-4 shadow-card">
        <div className="grid grid-cols-3 gap-3">
          <Mini label="Tokens held" value={formatUnits(holding.units)} />
          <Mini label="Already listed" value={formatUnits(holding.units_listed)} />
          <Mini label="Available" value={formatUnits(holding.units_available)} accent />
        </div>
      </section>

      <section className="space-y-4 rounded-card border border-border bg-surface p-5 shadow-card">
        <Field
          label="How many tokens do you want to sell?"
          hint={`You can list any part of the ${formatUnits(holding.units_available)} available. You keep the rest.`}
        >
          <input
            value={tokens}
            onChange={(e) => setTokens(e.target.value.replace(/[^\d.]/g, ''))}
            inputMode="decimal"
            placeholder="0"
            className="tabular h-11 w-full rounded-field border border-border bg-surface px-3 text-base text-ink outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
        </Field>

        {sums?.tooMuch ? (
          <p className="rounded-field bg-danger/10 px-3 py-2 text-micro text-danger">
            You have {formatUnits(holding.units_available)} available to list.
            {holding.units_listed !== '0.00000000'
              ? ` ${formatUnits(holding.units_listed)} is already on the marketplace.`
              : ''}
          </p>
        ) : null}

        <Field label={`Price per token (${quote})`} hint="What one token costs a buyer.">
          <div className="flex h-11 items-center rounded-field border border-border bg-surface px-3 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
            <span className="mr-2 text-sm text-ink-subtle">{quote}</span>
            <input
              value={price}
              onChange={(e) => setPrice(e.target.value.replace(/\D/g, ''))}
              inputMode="numeric"
              placeholder="0"
              className="tabular h-full w-full bg-transparent text-base text-ink outline-none"
            />
          </div>
        </Field>

        <Field
          label="Smallest a buyer may take"
          hint="Optional. Leave blank to require the whole listing in one go."
        >
          <input
            value={minimum}
            onChange={(e) => setMinimum(e.target.value.replace(/[^\d.]/g, ''))}
            inputMode="decimal"
            placeholder={tokens || '0'}
            className="tabular h-11 w-full rounded-field border border-border bg-surface px-3 text-base text-ink outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
        </Field>

        {sums?.minTooBig ? (
          <p className="rounded-field bg-danger/10 px-3 py-2 text-micro text-danger">
            The minimum cannot be more than the quantity you are listing.
          </p>
        ) : null}

        <Field label="Anything buyers should know" hint="Optional. Shown on the listing.">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="w-full rounded-field border border-border bg-surface px-3 py-2.5 text-base text-ink outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
        </Field>
      </section>

      {/* WHAT HAPPENS TO THE HOLDING. Both halves, so the split is the thing being agreed to. */}
      {tokens && sums && !sums.tooMuch ? (
        <section className="rounded-card bg-primary-tint p-4">
          <dl className="space-y-1.5">
            <Line k="Listing" v={`${formatUnits(tokens)} tokens`} />
            <Line k="Total if it all sells" v={`${quote} ${formatMinor(sums.total, quote)}`} />
            <Line k="You keep" v={`${formatUnits(unitsFromMinor(sums.remaining))} tokens`} />
          </dl>
        </section>
      ) : null}

      {error ? (
        <p className="rounded-field bg-danger/10 px-3 py-2 text-sm text-danger" role="alert">
          {error}
        </p>
      ) : null}

      <button
        onClick={() => void submit()}
        disabled={blocked || busy}
        className="w-full rounded-field bg-primary py-3 text-sm font-medium text-white transition hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-40"
      >
        {busy ? 'Listing...' : 'List on the marketplace'}
      </button>

      <p className="text-center text-micro text-ink-subtle">
        Listing does not transfer anything. The tokens stay yours until a buyer takes them, and you
        can withdraw the listing until then.
      </p>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-ink">{label}</span>
      {hint ? <span className="mt-0.5 block text-micro text-ink-subtle">{hint}</span> : null}
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Mini({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div>
      <p className="text-nano text-ink-subtle">{label}</p>
      <p className={`tabular truncate text-sm font-medium ${accent ? 'text-brand' : 'text-ink'}`}>
        {value}
      </p>
    </div>
  );
}

function Line({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-micro text-ink-muted">{k}</dt>
      <dd className="tabular text-sm font-medium text-ink">{v}</dd>
    </div>
  );
}

function BackIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
    </svg>
  );
}
